import React, { useRef, useCallback, useState } from 'react';

interface VirtualJoystickProps {
  /** Called on every touchmove with normalized direction (-1 to 1 per axis) */
  onDirectionChange: (direction: { x: number; y: number }) => void;
  /** Outer ring diameter in px (default 140) */
  size?: number;
}

const DEADZONE = 0.1; // Ignore tiny movements

const VirtualJoystick: React.FC<VirtualJoystickProps> = ({
  onDirectionChange,
  size = 140,
}) => {
  const knobSize = size * 0.4;
  const maxDisplacement = (size - knobSize) / 2;
  const activeTouchIdRef = useRef<number | null>(null);
  const centerRef = useRef({ x: 0, y: 0 });
  const [knobOffset, setKnobOffset] = useState({ x: 0, y: 0 });
  const [isActive, setIsActive] = useState(false);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (activeTouchIdRef.current !== null) return; // already tracking a finger

      const touch = e.changedTouches[0];
      activeTouchIdRef.current = touch.identifier;

      // Use the element's center as origin
      const rect = e.currentTarget.getBoundingClientRect();
      centerRef.current = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      setIsActive(true);
      setKnobOffset({ x: 0, y: 0 });
      onDirectionChange({ x: 0, y: 0 });
    },
    [onDirectionChange]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (activeTouchIdRef.current === null) return;

      // Find our tracked finger
      let touch: React.Touch | null = null;
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === activeTouchIdRef.current) {
          touch = e.changedTouches[i];
          break;
        }
      }
      if (!touch) return;

      const dx = touch.clientX - centerRef.current.x;
      const dy = touch.clientY - centerRef.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Clamp to ring radius
      const clampedDist = Math.min(dist, maxDisplacement);
      const angle = Math.atan2(dy, dx);

      const clampedX = Math.cos(angle) * clampedDist;
      const clampedY = Math.sin(angle) * clampedDist;

      setKnobOffset({ x: clampedX, y: clampedY });

      // Normalize to -1..1
      const nx = clampedX / maxDisplacement;
      const ny = clampedY / maxDisplacement;

      onDirectionChange({
        x: Math.abs(nx) > DEADZONE ? nx : 0,
        y: Math.abs(ny) > DEADZONE ? ny : 0,
      });
    },
    [maxDisplacement, onDirectionChange]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only release if our tracked finger was lifted
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === activeTouchIdRef.current) {
          activeTouchIdRef.current = null;
          setIsActive(false);
          setKnobOffset({ x: 0, y: 0 });
          onDirectionChange({ x: 0, y: 0 });
          return;
        }
      }
    },
    [onDirectionChange]
  );

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      style={{
        position: 'fixed',
        bottom: 'max(48px, 8vh)',
        left: 'max(24px, 3vw)',
        width: size,
        height: size,
        borderRadius: '50%',
        background: isActive
          ? 'rgba(255,255,255,0.2)'
          : 'rgba(255,255,255,0.1)',
        border: '2px solid rgba(255,255,255,0.25)',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.15s ease',
      }}
    >
      {/* Inner knob */}
      <div
        style={{
          width: knobSize,
          height: knobSize,
          borderRadius: '50%',
          background: isActive
            ? 'rgba(255,255,255,0.6)'
            : 'rgba(255,255,255,0.35)',
          border: '2px solid rgba(255,255,255,0.4)',
          transform: `translate(${knobOffset.x}px, ${knobOffset.y}px)`,
          transition: isActive ? 'none' : 'transform 0.15s ease',
          pointerEvents: 'none',
          boxShadow: isActive
            ? '0 0 12px rgba(255,255,255,0.3)'
            : 'none',
        }}
      />
    </div>
  );
};

export default VirtualJoystick;
